import { CachedFileItem } from "./CachedFileItem.js";
import { cloneExtendedSourceItem } from "./ExtendedSourceItem.js";
import { cloneFileItem } from "./FileItem.js";
import { mergeOptions } from "./Options.js";
import { appendArrayBuffer } from "./append/appendArrayBuffer.js";
import { appendFileList } from "./append/appendFileList.js";
import { appendPath } from "./append/appendPath.js";
import { appendSource } from "./append/appendSource.js";
import { appendText } from "./append/appendText.js";
import { appendWebSource } from "./append/appendWebSource.js";
import { fromIum } from "./fromIum.js";
import { fromZip } from './from_zip.js';
import { toIum } from "./toIum.js";
import { convertExtendedSourceToFile } from "./utilities/convertExtendedSourceToFile.js";
import { expandAndFilter } from "./utilities/expand/expandAndFilter.js";
import { filterFileCollection } from './utilities/filter_file_collection.js';
import { getNameInfo } from "./utilities/getNameInfo.js";
import { shouldAddItem } from "./utilities/shouldAddItem.js";
export class FileCollection {
    files;
    sources;
    options;
    constructor(options = {}, toClone) {
        this.options = toClone ? mergeOptions(toClone.options, options) : options;
        if (toClone) {
            this.files = toClone.files.map((file) => {
                file = cloneFileItem(file);
                return this.options.cache ? new CachedFileItem(file) : file;
            });
            this.sources = toClone.sources.map(cloneExtendedSourceItem);
        }
        else {
            this.files = [];
            this.sources = [];
        }
    }
    /**
     * This is unexpected to be used directly
     * @param source - The source to append, which should be an ExtendedSourceItem.
     * @private
     * @returns - A promise that resolves to the FileCollection instance.
     */
    async appendExtendedSource(source) {
        if (!shouldAddItem(source.relativePath, this.options.filter))
            return this;
        this.sources.push(source);
        const sourceFile = convertExtendedSourceToFile(source);
        const files = await expandAndFilter(sourceFile, this.options);
        const existingFiles = new Set(this.files.map((f) => f.relativePath));
        for (const file of files) {
            if (existingFiles.has(file.relativePath)) {
                throw new Error(`Duplicate relativePath: ${file.relativePath}`);
            }
            if (this.options.cache) {
                this.files.push(new CachedFileItem(file));
            }
            else {
                this.files.push(file);
            }
        }
        return this;
    }
    /**
     * Save an object to the collection.
     * This method will convert typed arrays to normal arrays and will
     * replace a potentially existing file with the same name.
     * @param key - The key is the relative path of the file in the collection.
     * @param value - The value is the object to save.
     *   It will be serialized using JSON.stringify.
     * @returns A promise that resolves when the file is saved.
     */
    /* eslint-disable @typescript-eslint/no-explicit-any */
    set(key, value) {
        this.removeFile(key);
        const string = JSON.stringify(value, (key, value) => ArrayBuffer.isView(value) ? Array.from(value) : value);
        return this.appendText(key, string);
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */
    removeFile(originalRelativePath) {
        const { relativePath } = getNameInfo(originalRelativePath);
        const index = this.files.findIndex((f) => f.relativePath === relativePath);
        let removedFile;
        if (index !== -1) {
            const file = this.files[index];
            const sourceUUID = file?.sourceUUID;
            removedFile = this.files.splice(index, 1)[0];
            // any other files with the same sourceUUID?
            if (this.files.some((f) => f.sourceUUID === sourceUUID))
                return;
            // delete the source
            const sourceIndex = this.sources.findIndex((s) => s.uuid === sourceUUID);
            if (sourceIndex !== -1) {
                this.sources.splice(sourceIndex, 1);
            }
            else {
                throw new Error(`Source not found for UUID: ${sourceUUID}`);
            }
        }
        return removedFile;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async get(key) {
        const { relativePath } = getNameInfo(key);
        const file = this.files.find((f) => f.relativePath === relativePath);
        if (!file) {
            throw new Error(`Key not found: ${key}`);
        }
        const string = await file.text();
        return JSON.parse(string);
    }
    async appendWebSource(webSourceURL, options = {}) {
        await appendWebSource(this, webSourceURL, options);
        return this;
    }
    /**
     * This method will append a list of files to the collection.
     * @param fileList - pass a FileList (from dom input file element or similar) or an iterable of File objects.
     * @returns - A promise that resolves when the files are appended.
     */
    async appendFileList(fileList) {
        await appendFileList(this, fileList);
        return this;
    }
    async appendSource(webSource, options = {}) {
        await appendSource(this, webSource, options);
        return this;
    }
    /**
     * This method can only be used from nodejs and will throw an error in the browser
     * @param path - The path to the file or directory to append.
     * @param options - Options for appending the path.
     * @param [options.keepBasename=true] - If true, the basename of the path will be kept as the relative path.
     * @returns A promise that resolves when the path is appended.
     */
    async appendPath(path, options = {}) {
        await appendPath(this, path, options);
        return this;
    }
    async appendText(relativePath, text, options = {}) {
        await appendText(this, relativePath, text, options);
        return this;
    }
    async appendArrayBuffer(relativePath, arrayBuffer, options = {}) {
        await appendArrayBuffer(this, relativePath, arrayBuffer, options);
        return this;
    }
    toIum(options = {}) {
        return toIum(this.alphabetical(), options);
    }
    static async fromIum(ium) {
        const fileCollection = await fromIum(ium);
        return fileCollection.alphabetical();
    }
    static async fromZip(zipContent, options = {}) {
        const collection = new FileCollection(options);
        await fromZip(collection, zipContent, options);
        collection.alphabetical();
        return collection;
    }
    static async fromPath(path, collectionOptions = {}, options = {}) {
        const collection = new FileCollection(collectionOptions);
        await collection.appendPath(path, options);
        return collection;
    }
    static async fromSource(source, collectionOptions = {}, options = {}) {
        const collection = new FileCollection(collectionOptions);
        await collection.appendSource(source, options);
        collection.alphabetical();
        return collection;
    }
    static fromCollection(collection, options) {
        return new FileCollection(options, collection);
    }
    alphabetical() {
        this.sources.sort((a, b) => (a.relativePath < b.relativePath ? -1 : 1));
        this.files.sort((a, b) => (a.relativePath < b.relativePath ? -1 : 1));
        return this;
    }
    [Symbol.iterator]() {
        return this.files.values();
    }
    /**
     * Filter files of the collection based on a predicate function.
     * @param predicate - Function which takes a FileItem, its index, and the array of FileItems, must return a boolean.
     *  True to keep the file, false to remove it.
     * @returns A new FileCollection containing only the files that match the predicate (and sources attached to these files).
     */
    filter(predicate) {
        return filterFileCollection(this, predicate, new FileCollection(this.options));
    }
}
//# sourceMappingURL=FileCollection.js.map